// Path: C:\gestion-copro\src\types\echeance.types.ts
// Types pour le module Calendrier / Échéances VIGIE
// Session 130 : Création — Échéances automatiques (garanties, courriers, dossiers) + manuelles

import { Timestamp } from 'firebase/firestore';

// ============================================
// SOURCES D'ÉCHÉANCES
// ============================================

/** Source de l'échéance — automatique (calculée) ou manuelle (créée par l'utilisateur) */
export type EcheanceSource = 'garantie' | 'courrier' | 'dossier' | 'manuelle';

// ============================================
// CATÉGORIES D'ÉCHÉANCES MANUELLES
// ============================================

export type EcheanceCategorieManuelle =
  | 'ag'                  // Assemblée générale
  | 'contrat_syndic'      // Renouvellement / résiliation contrat syndic
  | 'appel_fonds'         // Appel de fonds trimestriel
  | 'mise_en_demeure'     // Délai de réponse mise en demeure
  | 'prescription'        // Prescription légale (contestation AG, etc.)
  | 'assurance'           // Échéance assurance (PNO, MRH)
  | 'diagnostic'          // DPE, amiante, plomb...
  | 'travaux'             // Début/fin travaux votés
  | 'personnalise';       // Libre

export const ECHEANCE_CATEGORIES_MANUELLES: Record<EcheanceCategorieManuelle, string> = {
  ag: 'Assemblée générale',
  contrat_syndic: 'Contrat syndic',
  appel_fonds: 'Appel de fonds',
  mise_en_demeure: 'Mise en demeure',
  prescription: 'Prescription légale',
  assurance: 'Assurance',
  diagnostic: 'Diagnostic',
  travaux: 'Travaux',
  personnalise: 'Personnalisé',
};

// ============================================
// NIVEAUX D'URGENCE — Grille Principe 9
// ============================================

export type EcheanceUrgence =
  | 'serein'          // > 6 mois
  | 'alerter'         // 3-6 mois
  | 'pousser'         // 1-3 mois
  | 'urgent'          // < 30 jours
  | 'urgence_absolue' // < 7 jours
  | 'depasse';        // Dépassé

export const ECHEANCE_URGENCE_CONFIG: Record<EcheanceUrgence, {
  label: string;
  couleurBg: string;
  couleurTexte: string;
  couleurBorder: string;
  couleurDot: string;
}> = {
  serein: {
    label: 'Serein',
    couleurBg: 'bg-green-50',
    couleurTexte: 'text-green-700',
    couleurBorder: 'border-green-200',
    couleurDot: 'bg-green-500',
  },
  alerter: {
    label: 'À surveiller',
    couleurBg: 'bg-amber-50',
    couleurTexte: 'text-amber-700',
    couleurBorder: 'border-amber-200',
    couleurDot: 'bg-amber-500',
  },
  pousser: {
    label: 'Attention',
    couleurBg: 'bg-orange-50',
    couleurTexte: 'text-orange-700',
    couleurBorder: 'border-orange-200',
    couleurDot: 'bg-orange-500',
  },
  urgent: {
    label: 'Urgent',
    couleurBg: 'bg-red-50',
    couleurTexte: 'text-red-700',
    couleurBorder: 'border-red-200',
    couleurDot: 'bg-red-500',
  },
  urgence_absolue: {
    label: 'Urgence absolue',
    couleurBg: 'bg-red-100',
    couleurTexte: 'text-red-800',
    couleurBorder: 'border-red-300',
    couleurDot: 'bg-red-700',
  },
  depasse: {
    label: 'Dépassé',
    couleurBg: 'bg-gray-100',
    couleurTexte: 'text-red-600',
    couleurBorder: 'border-gray-300',
    couleurDot: 'bg-gray-500',
  },
};

// ============================================
// INTERFACE PRINCIPALE — ÉCHÉANCE
// ============================================

export interface Echeance {
  id: string;
  coproprieteId: string;

  /** Titre affiché */
  titre: string;

  /** Description optionnelle */
  description?: string;

  /** Date de l'échéance */
  dateEcheance: Timestamp;

  /** Source : automatique ou manuelle */
  source: EcheanceSource;

  /** Catégorie (uniquement pour les manuelles) */
  categorie?: EcheanceCategorieManuelle;

  /** Référence vers l'objet source (ID du courrier, dossier, etc.) */
  sourceId?: string;

  /** Label lisible de la source ("GPA", "Courrier à KIC", "Dossier Faïence") */
  sourceLabel?: string;

  /** Urgence calculée — dérivée de dateEcheance vs aujourd'hui */
  urgence?: EcheanceUrgence;

  /** Jours restants — calculé côté client */
  joursRestants?: number;

  /** Échéance terminée / traitée */
  terminee: boolean;

  /**
   * Session 179 : Marque l'échéance comme résolue (sujet traité).
   * Distinct de 'terminee' : une échéance peut être passée (terminee) sans que
   * le sujet soit résolu, et inversement. Les éléments resolu=true sont exclus
   * du contexte SENTINELLE.
   * Rétrocompatible : absent ou false = non résolu.
   */
  resolu?: boolean;

  /** Rappel activé (pour futures notifications) */
  rappel?: boolean;

  /** Jours avant l'échéance pour le rappel */
  rappelJoursAvant?: number;

  /** Métadonnées */
  createdAt: Timestamp;
  updatedAt?: Timestamp;
  createdBy?: string;
}

// ============================================
// FORME DE CRÉATION (sans id, sans timestamps)
// ============================================

export type EcheanceCreate = Omit<Echeance, 'id' | 'createdAt' | 'updatedAt' | 'urgence' | 'joursRestants'>;

// ============================================
// UTILITAIRE — Calcul urgence depuis jours restants
// ============================================

export const calculerUrgence = (joursRestants: number): EcheanceUrgence => {
  if (joursRestants <= 0) return 'depasse';
  if (joursRestants <= 7) return 'urgence_absolue';
  if (joursRestants <= 30) return 'urgent';
  if (joursRestants <= 90) return 'pousser';
  if (joursRestants <= 180) return 'alerter';
  return 'serein';
};

/** Calcul des jours restants depuis une date */
export const calculerJoursRestants = (dateEcheance: Date): number => {
  const maintenant = new Date();
  maintenant.setHours(0, 0, 0, 0);
  const cible = new Date(dateEcheance);
  cible.setHours(0, 0, 0, 0);
  return Math.ceil((cible.getTime() - maintenant.getTime()) / (1000 * 60 * 60 * 24));
};
